import { useRef, useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useLandingConfig } from "../../hooks/useLandingConfig";
import { fileToCompressedDataURL } from "../../lib/imageUtils";
import { Save, Heart, Upload, X, ArrowLeft, ArrowRight, ImageIcon } from "lucide-react";

const MAX_PHOTOS = 12;

export default function ReviewsConfigPage() {
    const { config, setConfig, loading, saving, save } = useLandingConfig();
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [processing, setProcessing] = useState(false);

    const photos = Array.isArray(config.review_photos) ? config.review_photos : [];
    const setPhotos = (next) => setConfig((c) => ({ ...c, review_photos: next }));

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        const room = MAX_PHOTOS - photos.length;
        if (room <= 0) return toast.error(`最多只能上传 ${MAX_PHOTOS} 张好评图`);
        const arr = Array.from(files).slice(0, room);
        if (files.length > room) toast.info(`最多 ${MAX_PHOTOS} 张，已省略多余的 ${files.length - room} 张`);
        setProcessing(true);
        try {
            const added = [];
            for (const f of arr) {
                if (!f.type.startsWith("image/")) continue;
                added.push(await fileToCompressedDataURL(f));
            }
            if (added.length) setPhotos([...photos, ...added].slice(0, MAX_PHOTOS));
        } catch (e) {
            toast.error(e.message || "上传失败");
        } finally {
            setProcessing(false);
        }
    };

    const remove = (idx) => setPhotos(photos.filter((_, i) => i !== idx));
    const move = (from, to) => {
        if (to < 0 || to >= photos.length) return;
        const next = [...photos];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        setPhotos(next);
    };

    if (loading) return <div className="text-gold-500">加载中...</div>;

    return (
        <div className="space-y-6" data-testid="reviews-config-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">落地页</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1 flex items-center gap-3">
                        <Heart className="h-7 w-7 text-gold-600" /> 顾客好评管理
                    </h1>
                    <p className="text-sm text-gold-500 mt-1">
                        纯照片墙 · 上传评价截图或买家秀，顺序即落地页展示顺序 · 留空则整段隐藏
                    </p>
                </div>
                <Button
                    data-testid="reviews-save-btn-top"
                    onClick={() => save()}
                    disabled={saving}
                    className="h-10 px-5 bg-gold-600 hover:bg-gold-700 text-white gap-2 shrink-0"
                >
                    <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存"}
                </Button>
            </div>

            <Card className="p-6 border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">好评照片墙</h2>
                    <Badge className="bg-gold-100 text-gold-800 border-0" data-testid="reviews-count-badge">
                        {photos.length} / {MAX_PHOTOS} 张
                    </Badge>
                </div>
                <p className="text-xs text-slate-500">
                    建议使用接近 4:5 的竖图 · 自动压缩至 900px · 第一张显示在最前面
                </p>

                {photos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {photos.map((url, idx) => (
                            <div
                                key={`${idx}-${url.slice(0, 24)}`}
                                data-testid={`review-photo-${idx}`}
                                className="relative aspect-[4/5] bg-gold-50 border border-slate-200 rounded-md overflow-hidden group"
                            >
                                <img src={url} alt={`好评 ${idx + 1}`} className="w-full h-full object-cover" />
                                <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-slate-900/70 text-white text-[10px] font-bold">
                                    #{idx + 1}
                                </div>
                                <button
                                    type="button"
                                    data-testid={`review-photo-remove-${idx}`}
                                    onClick={() => remove(idx)}
                                    className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-slate-900/80 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                                    title="删除"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                                <div className="absolute inset-x-1.5 bottom-1.5 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        type="button"
                                        data-testid={`review-photo-left-${idx}`}
                                        onClick={() => move(idx, idx - 1)}
                                        disabled={idx === 0}
                                        className="h-7 w-7 rounded-full bg-white/95 text-slate-700 flex items-center justify-center shadow disabled:opacity-30 hover:bg-gold-100"
                                        title="向前"
                                    >
                                        <ArrowLeft className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`review-photo-right-${idx}`}
                                        onClick={() => move(idx, idx + 1)}
                                        disabled={idx === photos.length - 1}
                                        className="h-7 w-7 rounded-full bg-white/95 text-slate-700 flex items-center justify-center shadow disabled:opacity-30 hover:bg-gold-100"
                                        title="向后"
                                    >
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {photos.length < MAX_PHOTOS && (
                            <button
                                type="button"
                                data-testid="review-photo-add-more"
                                onClick={() => inputRef.current?.click()}
                                className="aspect-[4/5] rounded-md border-2 border-dashed border-slate-300 bg-gold-50 hover:border-gold-400 flex flex-col items-center justify-center text-gold-600 transition-colors"
                            >
                                <Upload className="h-5 w-5 mb-1" />
                                <span className="text-xs font-medium">再加一张</span>
                            </button>
                        )}
                    </div>
                )}

                {photos.length === 0 && (
                    <div
                        role="button"
                        tabIndex={0}
                        data-testid="review-photos-dropzone"
                        onClick={() => inputRef.current?.click()}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer?.files); }}
                        className={`flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed rounded-md cursor-pointer transition-colors ${
                            dragOver ? "border-gold-500 bg-gold-50" : "border-slate-300 bg-gold-50 hover:border-gold-400"
                        }`}
                    >
                        {processing ? (
                            <div className="text-sm text-gold-500">处理中...</div>
                        ) : (
                            <>
                                <div className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-2">
                                    <Upload className="h-4 w-4 text-gold-500" />
                                </div>
                                <div className="text-sm font-medium text-slate-700">
                                    拖放好评图到这里，或<span className="text-gold-600">点击选择</span>
                                </div>
                                <div className="text-xs text-gold-500 mt-1 flex items-center gap-1">
                                    <ImageIcon className="h-3 w-3" /> JPG / PNG · 最多 {MAX_PHOTOS} 张 · 建议 4:5 竖图
                                </div>
                            </>
                        )}
                    </div>
                )}

                <input
                    ref={inputRef}
                    data-testid="review-photos-file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => { if (e.target.files) { handleFiles(e.target.files); e.target.value = ""; } }}
                />
            </Card>

            <Card className="p-4 border-dashed border-gold-300 bg-gold-50/40">
                <div className="text-xs font-semibold uppercase tracking-wider text-gold-700 mb-2">开发者接口</div>
                <div className="text-xs font-mono text-slate-700 space-y-1">
                    <div>公开读取：<code className="bg-white/80 px-1.5 py-0.5 rounded border border-gold-200">GET /api/public/landing-config</code></div>
                    <div>字段：<code className="bg-white/80 px-1.5 py-0.5 rounded border border-gold-200">review_photos: string[]</code> · 空数组时落地页隐藏整段</div>
                </div>
            </Card>

            <div className="fixed bottom-6 right-6 z-40">
                <Button data-testid="reviews-save-btn" onClick={() => save()} disabled={saving}
                    className="h-12 px-6 bg-gold-600 hover:bg-gold-700 text-white gap-2 shadow-xl shadow-gold-900/20 rounded-full">
                    <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存顾客好评"}
                </Button>
            </div>
        </div>
    );
}
